import {Link, useNavigate} from "react-router-dom";
import moment from "moment/moment";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Icon from "shared/ui/Icon";
import {getRomanNumber} from "utils/functions";
import {useAuth} from "hooks/useAuth";
import CustomButton from "../../../shared/ui/CustomButton";
import React from "react";

interface IDMessageContent {
    subject: string;
    expires: number | string;
    createdAt?: Date;
    startsAt: string;
    name?: string;
    id: number;
    grade: string;
    mode: number | string;
    classroom?: string;
    classroom_id: number;
    classroom_grade: number;
    type: string;
    file: {fileName: string, filePath: string} | null;
}

const getModeText = (mode: number | string) => {
    if (mode === 2) return "de antrenament";
    return "";
};

const getSubjectText = (mode: number | string, subject: string, grade: string) => {
    if (typeof (mode) === "number") {
        if (mode === 3 || mode === 4) return grade;
        else return `${subject} (${grade})`;
    } else return subject;
};

const getLink = (mode: number | string) => {
    if (mode === 1) return <Link to={"/teste"}>Teste școlare</Link>;
    if (mode === 2) return <Link to={"/teste-antrenament"}>Teste antrenament</Link>;
    if (mode === 3) return <Link to={"/teste-bac"}>Teste bacalaureat</Link>;
    if (mode === 4) return <Link to={"/teste-evaluare"}>Teste evaluare</Link>;
    if (mode === 5) return <Link to={"/teste-literatie"}>Teste literație</Link>;
    if (mode === 6) return <Link to={"/teste-literatie-digitala"}>Teste literație digitală</Link>;
    if (mode === 7) return <Link to={"/teste-proprii"}>Teste proprii</Link>;
    if (mode === 8) return <Link to={"/teste-literatie-numerica"}>Teste literație numerică</Link>;
    if (mode === 9) return <Link to={"/teste-literatie-stiintifica"}>Teste literație Științifică</Link>;
}

const MessagesContent = (data: IDMessageContent) => {
    const {user} = useAuth();
    const navigate = useNavigate();
    const romanGrade = data.classroom_grade ? ` (${getRomanNumber(data.classroom_grade)})` : "";
    const modeText = getModeText(data.mode);
    const link = getLink(data.mode);
    const subjectText = getSubjectText(data.mode, data.subject, data.grade);
    const testDate = moment(data.createdAt).locale("ro").format("DD-MM-YYYY");
    const testHour = moment(data.createdAt).locale("ro").format("HH:mm");
    const startsAtText = data.startsAt ? (
        <>
            {user.position === "teacher" ? "pot fi accesate " : "poate fi accesat "} începând cu data
            de <b>{moment(data.startsAt).locale("ro").format("DD-MM-YYYY")} </b>
            la ora <b>{moment(data.startsAt).locale("ro").format("HH:mm")}</b> pentru un interval
            de <b>{data.expires}</b> ore
        </>
    ) : (<>expiră în <b>{data.expires}</b> ore</>);

    const downloadFile = (filePath: string) => {
        const link = document.createElement("a");
        link.href = filePath;
        link.download = filePath.split("/").pop() || "export.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderContent = () => {
        switch (data.type) {
            case 'TEST_SESSION_CLOSED':
                return user.position === "teacher" ? (
                    <>
                        Elevul <b>{data.name}</b> din clasa <b>({romanGrade}) {data.classroom}</b> a finalizat sesiunea
                        de testare <b>{modeText} {data.mode === 7 ? "proprie" : ""}</b> la <b>{subjectText}</b>, generată în data de <b>{testDate}</b>, la
                        ora <b> {testHour}</b>. Rezultatele sale pot fi accesate în secțiunea <b>Teste Finalizate</b> a
                        contului tău.
                    </>
                ) : null;

            case 'TEST_SESSION_EXPIRED':
                return user.position === "student" ? (
                    <>
                        Sesiunea de testare <b>{modeText} {data.mode === 7 ? "proprie" : ""}</b> la <b>{data.subject}</b>, generată de <b>{data.name}</b> a
                        expirat. Contactează-ți profesorul coordonator pentru mai multe detalii.
                    </>
                ) : null;

            case 'TEST_SESSION_STARTED':
                return user.position === "teacher" ? (
                    <>
                        Elevul <b>{data.name}</b> din clasa <b>{romanGrade} {data.classroom}</b> a accesat sesiunea de
                        testare <b>{modeText} {data.mode === 7 ? "proprie" : ""}</b> la <b>{data.subject}</b>, generată în data de <b>{testDate}</b>, la
                        ora <b>{testHour}</b>.
                    </>
                ) : null;

            case 'TEST_SESSION_CREATED':
                if (user.position === "student") return (
                    <>
                        <Typography>Testul <b>{modeText} {data.mode === 7 ? "propriu" : ""}</b> de <b>{subjectText}</b>,
                            clasa <b>{romanGrade} {data.classroom}</b>, {startsAtText}.</Typography>
                        <Button
                            variant="contained"
                            startIcon={<Icon icon="far pen-to-square" fixedWidth={true} sx={{fontSize: "1.25rem"}}/>}
                            onClick={() => {
                                navigate(`/teste/${data.id}`)
                            }}
                        >
                            Începe testul
                        </Button>
                    </>
                );
                if (user.position === "teacher") return (
                    <>
                        <Typography>
                            Testele <b>{modeText} {data.mode === 7 ? "proprii" : ""}</b> de <b>{subjectText}</b> pentru elevii
                            din <b>{romanGrade} {data.classroom}</b>, {startsAtText}.
                        </Typography>
                        <Typography>Ele pot fi vizualizate in pagina {link}.</Typography>
                    </>
                );
                return null;

            case 'EXPORT_CREATED':
                return (
                    <>
                        <Typography>
                            Apasă butonul de mai jos pentru descărcarea tabelului <b>{data.file?.fileName}</b>.</Typography>
                        <Typography variant="body1"><b>
                            Atenție! Fișierul este valabil 24 de ore.
                        </b></Typography>
                        <CustomButton
                            style={{fontWeight: 500}}
                            buttonType="textIcon"
                            bgcolor="dark"
                            onClick={() => downloadFile(data.file?.filePath || "")}
                            icon={<Icon icon="far arrow-down-to-line" fixedWidth={true}/>}
                        >
                            Descarcă fișierul
                        </CustomButton>
                    </>
                );
            default:
                return null;
        }
    }

    return <>{renderContent()}</>;
};

export default MessagesContent;
